import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Stack, Typography, Divider, InputAdornment } from '@mui/material';

import { PaperCard } from 'components/Card';
import { FormProvider, MultiSelect, TextField } from 'components/Form';
import { useForm } from 'react-hook-form';
import { AccordionListItem } from 'components/Button';
import { useParams } from 'react-router-dom';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useUpdateCompanyProfile } from '../api/updateCompanyProfile';
import { useCreateCompanyProfile } from '../api/createCompanyProfile';
import { useUpdateCompanyCompetenceAreas } from '../api/updateCompanyCompetenceAreas';
import { useCompetenceAreas } from 'api/getCompetenceAreas';
import { companyProfileSchema } from '../validation';
import { DELEGON_URL } from 'configuration';

const getDefaultValues = () => ({
  aboutUs: '',
  website: '',
  slug: '',
  competenceAreas: [],
});

export const EditForm = ({company, refetch}) => { 
  let { id } = useParams();

  if (!id || id.length === 0) {
    id = company?.company_id;
  }

  const CompanyProfile = company?.CompanyProfile;
  const isSupplier = company?.Type === 'supplier';

  const [open, setOpen] = useState(false);

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: useYupValidationResolver(companyProfileSchema),
  });

  const { mutateAsync: mutateAsyncUpdate } = useUpdateCompanyProfile(refetch);
  const { mutateAsync: mutateAsyncCreate } = useCreateCompanyProfile(refetch);
  const { mutateAsync: mutateAsyncCompetenceAreas } = useUpdateCompanyCompetenceAreas();
  const { data: competences } = useCompetenceAreas();

  const handleClose = () => {
    setOpen(false);
    methods.reset({ ...getDefaultValues() });
  };

  const onSubmit = (values) => {
    const saveCompetenceAreas = isSupplier
      ? mutateAsyncCompetenceAreas({
          companyID: id,
          competenceAreaIDs: (values.competenceAreas || []).map((area) => area.id),
        })
      : Promise.resolve();

    saveCompetenceAreas
      .then(() =>
        CompanyProfile?.profile_id
          ? mutateAsyncUpdate({
              ...values,
              companyID: id,
              profileID: CompanyProfile?.profile_id,
            })
          : mutateAsyncCreate({
              ...values,
              companyID: id,
            })
      )
      .then(handleClose)
      .catch(() => {});
  };

  useEffect(() => {
    if (!open) {
      methods.reset({ ...CompanyProfile });
      document.body.style.overflow = '';
    } else if (open) {
      const {
        about_us,
        website,
        slug,
        competence_areas,
      } = CompanyProfile;

      methods.reset({
        aboutUs: about_us,
        website: website,
        slug: slug,
        competenceAreas: competence_areas || [],
      });

      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const EditFormContent = (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          background: '#F3F4F6',
          justifyContent: 'center',
          display: 'flex',
          zIndex: (theme) => theme.zIndex.modal,
          alignItems: 'flex-start',
          overflow: 'hidden',
        }}
      >
        <Stack
          sx={{
            position: 'relative',
            width: '100%',
            height: `calc(100% - 8 * 26.25px)`,
            marginTop: 26.25,
            overflowY: 'auto',
            backgroundColor: '#fff',
          }}
        >
          <FormProvider methods={methods} onSubmit={onSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <PaperCard
              height={69}
              sx={{
                background: '#fff',
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                px: 4,
                py: 2,
                border: 0,
                borderRadius: 0,
                boxShadow: 26,
                display: 'flex',
                boxSizing: 'border-box',
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography>Editing ‘{company?.Name}’ public profile</Typography>
                <Box
                  sx={{
                    width: 165,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    size="medium"
                    onClick={() => setOpen(false)}
                    variant="contained"
                    sx={({ palette }) => ({
                      width: 82,
                      boxShadow: 25,
                      color: palette.primary.main,
                      backgroundColor: '#fff',
                    })}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="medium"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ width: 67 }}
                  >
                    Save
                  </Button>
                </Box>
              </Stack>
            </PaperCard>

            <Stack
              direction="row"
              width="100%"
              minHeight={`calc(100vh - 8 * 26.25px)`}
              height="100%"
              bgcolor={'#fff'}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Stack width={'50%'} direction="column" px={8} py={4}>
                <TextField
                  name="aboutUs"
                  label="About us"
                  placeholder="Please enter about us"
                  type="text"
                  fullWidth
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-root, .MuiInputBase-input': {
                      p: 0,
                    },
                  }}
                  multiline
                  minRows={2}
                />
              </Stack>

              <Stack
                width={'50%'}
                direction="column"
                px={8}
                py={4}
                gap={2}
              >
                <TextField
                  name="website"
                  label="Website"
                  placeholder="Please enter website"
                  type="text"
                  fullWidth
                />
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={({ palette }) => ({
                          mr:"0",
                          '& .MuiTypography-root': {
                            color: palette.grey[400],
                          },
                          })
                        }
                      >
                        {DELEGON_URL}/profile/{company?.Type}/
                      </InputAdornment>
                    ),
                  }}
                  name="slug"
                  label="Profile URL"
                  placeholder="Enter your custom endpoint"
                  required
                  type="text"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': {
                      pl: "0!important",
                    },
                  }}
                />
                {isSupplier && (
                  <MultiSelect
                    multiple
                    name="competenceAreas"
                    label="Competence Areas"
                    placeholder="Please select"
                    fullWidth
                    value={CompanyProfile?.competence_areas || []}
                    options={competences?.competences || []}
                    onChange={(value) => methods.setValue('competenceAreas', value)}
                  />
                )}
              </Stack>
            </Stack>
            </LocalizationProvider>
          </FormProvider>
        </Stack>
      </Box>
  );

  return (
    <>
      <AccordionListItem onClick={() => setOpen(true)}>Edit Profile</AccordionListItem>
      {open && ReactDOM.createPortal(EditFormContent, document.body)}
    </>
  );
};
